import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2, Play } from '@digital-twins/design-system/icons'
import { SocialUniverseItem } from '@digital-twins/shared-types'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'

import { SocialCreditsCta } from './SocialCreditsCta'
import { topicCreditBanner } from './socialCredits'

interface SocialTopicCreditsNoticeProps {
  universe: SocialUniverseItem
  /** Clock for the 30-day auto-resume window (from `useNow`). */
  now: number
  isOrgOwner: boolean
  canManage: boolean
  isResuming: boolean
  /** Resumes the channels paused for credits (same resume call, funds checked by the server). */
  onResume: () => void
}

/**
 * The Topic header's credit notice (design S2, S3): one slim line when
 * credits run low or cannot cover the next update, a compact warning banner
 * when a channel is paused for credits (auto-resume or Resume) or for
 * maintenance. Nothing otherwise, and nothing credit related while charging
 * is off.
 */
export function SocialTopicCreditsNotice(props: SocialTopicCreditsNoticeProps) {
  const { universe, now, isOrgOwner, canManage, isResuming, onResume } = props

  const { t } = useTranslation('app')

  const { kind } = topicCreditBanner(universe, now)

  if (kind === null) {
    return null
  }

  if (kind === 'low' || kind === 'insufficient') {
    return (
      <div
        className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]"
        data-testid="social-topic-credits-low"
        data-kind={kind}
      >
        <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-chart-warning" />
        <Text as="span" size="sm" color="muted" className="text-[13px]">
          {t(
            kind === 'low'
              ? 'socialListening.credits.topic.low'
              : 'socialListening.credits.topic.insufficient',
          )}
        </Text>
        <SocialCreditsCta
          isOrgOwner={isOrgOwner}
          testId="social-topic-credits-low"
          variant="inline"
          showMemberLine={false}
        />
      </div>
    )
  }

  const isOps = kind === 'ops'
  const title = isOps
    ? t('socialListening.credits.banner.maintenanceTitle')
    : t('socialListening.credits.banner.title')
  const body = isOps
    ? t('socialListening.credits.banner.maintenanceBody')
    : kind === 'auto'
      ? isOrgOwner
        ? t('socialListening.credits.banner.autoResume')
        : `${t('socialListening.credits.banner.autoResume')} ${t('socialListening.credits.banner.askOwner')}`
      : isOrgOwner
        ? t('socialListening.credits.banner.resume')
        : t('socialListening.credits.banner.resumeMember')

  const showResume = kind === 'resume' && canManage
  const showContactSales = !isOps && isOrgOwner

  return (
    <Alert
      variant="warning"
      role="status"
      className="mt-3 max-w-[640px] p-3 [&>svg]:left-3 [&>svg]:top-3"
      data-testid="social-topic-credits-banner"
      data-kind={kind}
    >
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <Text as="p" size="sm" className="font-medium text-foreground">
          {title}
        </Text>
        <Text as="p" size="sm" color="muted">
          {body}
        </Text>
        {showResume || showContactSales ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {showResume ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={isResuming}
                data-testid="social-topic-credits-resume"
                onClick={onResume}
              >
                {isResuming ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Play className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {t('socialListening.credits.resume')}
              </Button>
            ) : null}
            {showContactSales ? (
              <SocialCreditsCta
                isOrgOwner
                testId="social-topic-credits"
                variant={showResume ? 'ghost' : 'outline'}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </Alert>
  )
}
